import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n/index';

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [lang, setLang] = useState<'EN' | 'TR'>(i18n.language === 'tr' ? 'TR' : 'EN');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggleLang = () => {
    const next = lang === 'EN' ? 'TR' : 'EN';
    setLang(next);
    i18n.changeLanguage(next.toLowerCase());
  };

  const navLinks = [
    { label: t('nav_home'), href: '/' },
    { label: t('nav_about'), href: '/about' },
    { label: t('nav_electrical'), href: '/electrical' },
    { label: t('nav_construction'), href: '/construction' },
    { label: t('nav_projects'), href: '/projects' },
    { label: t('nav_contact'), href: '/contact' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-20">
        {/* Logo */}
        <a href="/" className="flex items-center cursor-pointer">
          <img
            src="https://can-el.com/common/img/canellogo.jpg"
            alt="CAN-EL Logo"
            className="h-12 w-auto object-contain transition-all duration-300"
          />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className={`text-sm font-medium tracking-wide transition-colors cursor-pointer whitespace-nowrap ${
                scrolled
                  ? 'text-gray-700 hover:text-[#E8A020]'
                  : 'text-white/90 hover:text-white'
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* Right side */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://tr.linkedin.com/company/canel-electric"
            target="_blank"
            rel="noopener noreferrer"
            className="w-8 h-8 flex items-center justify-center rounded-md bg-[#0A66C2] hover:bg-[#004182] transition-colors cursor-pointer"
          >
            <i className="ri-linkedin-fill text-white text-base" />
          </a>
          <button
            onClick={toggleLang}
            className={`text-xs font-semibold tracking-widest border px-3 py-1 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
              scrolled
                ? 'border-gray-300 text-gray-600 hover:border-[#E8A020] hover:text-[#E8A020]'
                : 'border-white/50 text-white hover:border-white'
            }`}
          >
            {lang === 'EN' ? 'TR' : 'EN'}
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden w-8 h-8 flex flex-col justify-center gap-1.5 cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span className={`block h-0.5 w-6 transition-all ${scrolled ? 'bg-gray-800' : 'bg-white'}`} />
          <span className={`block h-0.5 w-6 transition-all ${scrolled ? 'bg-gray-800' : 'bg-white'}`} />
          <span className={`block h-0.5 w-4 transition-all ${scrolled ? 'bg-gray-800' : 'bg-white'}`} />
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-gray-700 font-medium text-sm hover:text-[#E8A020] cursor-pointer"
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
            </a>
          ))}
          <div className="flex items-center gap-3">
            <a
              href="https://tr.linkedin.com/company/canel-electric"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 flex items-center justify-center rounded-md bg-[#0A66C2] hover:bg-[#004182] transition-colors cursor-pointer"
            >
              <i className="ri-linkedin-fill text-white text-base" />
            </a>
            <button
              onClick={toggleLang}
              className="text-xs font-semibold tracking-widest border border-gray-300 text-gray-600 px-3 py-1 rounded-full w-fit cursor-pointer whitespace-nowrap"
            >
              {lang === 'EN' ? 'TR' : 'EN'}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
